import { useEffect, useRef, useState } from 'react';

const METHODS = [
  {
    num: '01',
    icon: 'ri-car-line',
    title: '出張買取',
    description:
      'スタッフがご自宅や指定場所に伺い、その場で査定・買取いたします。大型家具・大量の品物がある場合に特におすすめです。',
    merit: '岡山市・近隣市町村対応',
  },
  {
    num: '02',
    icon: 'ri-send-plane-line',
    title: '宅配買取',
    description:
      '品物を梱包して送るだけ。送料は当社負担です。遠方の方や外出が難しい方でも安心してご利用いただけます。',
    merit: '送料無料・全国対応',
  },
];

const STEPS = [
  {
    step: 'STEP 1',
    icon: 'ri-phone-line',
    label: 'お電話・お問い合わせ',
    desc: 'まずはお電話またはお問い合わせフォームより出張買取のご依頼をお申し込みください。ご希望の日時・ご住所・お売りいただく品物の概要をお知らせいただければ、スタッフが丁寧にご対応いたします。事前のご相談だけでも構いませんので、どうぞお気軽にご連絡ください。',
    image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/e1551c5dab2c5de1e14877f8c3350860.webp',
  },
  {
    step: 'STEP 2',
    icon: 'ri-search-eye-line',
    label: '査定',
    desc: '経験豊富な専門スタッフが、お持ちいただいた品物を一点ずつ丁寧に査定いたします。査定は完全無料で、お時間もできる限り短縮できるよう努めております。査定中に気になる点があればお気軽にご質問ください。査定額にご納得いただけない場合でも、キャンセルは一切無料ですのでご安心ください。',
    image: 'https://public.readdy.ai/ai/img_res/a4de5a63b6d10882979643bff18b316d.jpg',
  },
  {
    step: 'STEP 3',
    icon: 'ri-shake-hands-line',
    label: 'ご成約・お支払い',
    desc: '査定額にご同意いただけましたらご成約となります。必要書類（本人確認書類など）にご署名いただいた後、即日現金またはお振込みにてお支払いいたします。振込をご希望の場合は口座情報をお知らせください。ご不明な点はスタッフまでお気軽にお申し付けください。',
    image: 'https://readdy.ai/api/search-image?query=A%20cheerful%20flat%20style%20illustration%20of%20a%20handshake%20between%20a%20customer%20and%20staff%20at%20a%20shop%20counter%2C%20cash%20and%20receipt%20on%20counter%2C%20happy%20smiling%20people%2C%20soft%20pastel%20green%20and%20white%20color%20palette%2C%20clean%20simple%20background%2C%20celebratory%20deal%20complete%20scene&width=480&height=320&seq=howto-step3-v2&orientation=landscape',
  },
];

const REQUIREMENTS = [
  { icon: 'ri-id-card-line', text: '本人確認書類（運転免許証・マイナンバーカード・健康保険証など）' },
  { icon: 'ri-archive-line', text: '買取品本体（状態を問わずご持参ください）' },
  { icon: 'ri-file-text-line', text: '付属品・保証書・ケース（あれば査定額アップの可能性あり）' },
  { icon: 'ri-bank-card-line', text: '振込希望の方は銀行口座情報（口座番号等）' },
];

const HowToSection = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="howto" ref={ref} className="py-20 md:py-28 bg-[#F8F9F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#B2AC88] text-xs font-medium tracking-widest uppercase">How To Sell</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2F3630] mt-3 mb-4" style={{ fontFamily: "'Shippori Mincho', serif" }}>
            2つの買取方法
          </h2>
          <p className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto">
            お客様のライフスタイルに合わせてお選びください
          </p>
        </div>

        {/* Method Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {METHODS.map((m, i) => (
            <div
              key={m.num}
              className={`relative bg-white rounded-2xl p-7 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex items-start justify-between mb-5">
                <span className="text-5xl font-bold text-[#B2AC88]/20" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {m.num}
                </span>
                <div className="w-12 h-12 flex items-center justify-center bg-[#B2AC88] rounded-xl">
                  <i className={`${m.icon} text-white text-xl`} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#2F3630] mb-3">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{m.description}</p>
              <span className="inline-flex items-center gap-1.5 bg-[#B2AC88] text-white text-xs px-3 py-1 rounded-full font-medium">
                <i className="ri-check-line" />
                {m.merit}
              </span>
            </div>
          ))}
        </div>

        {/* Process Flow */}
        <div
          className={`bg-white rounded-2xl p-8 md:p-12 mb-10 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-xl md:text-2xl font-bold text-[#2F3630] text-center mb-10" style={{ fontFamily: "'Shippori Mincho', serif" }}>
            出張買取の流れ
          </h3>
          <div className="flex flex-col gap-8">
            {STEPS.map((step, i) => (
              <div key={step.step} className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
                {/* Content */}
                <div className="flex-1 flex items-start gap-5">
                  <div className="flex-shrink-0 flex flex-col items-center gap-2">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#B2AC88] rounded-full">
                      <i className={`${step.icon} text-white text-xl`} />
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="w-0.5 h-8 bg-[#B2AC88]/30" />
                    )}
                  </div>
                  <div className="pt-1">
                    <p className="text-[#B2AC88] text-xs font-bold tracking-widest mb-1">{step.step}</p>
                    <p className="text-[#2F3630] font-bold text-lg mb-2">{step.label}</p>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
                {/* Image */}
                <div className="w-full md:w-64 h-44 flex-shrink-0 rounded-xl overflow-hidden">
                  <img
                    src={step.image}
                    alt={step.label}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements */}
        <div
          className={`border border-[#B2AC88]/30 rounded-2xl p-7 md:p-10 transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 flex items-center justify-center bg-[#B2AC88] rounded-lg">
              <i className="ri-list-check text-white text-base" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-[#2F3630]">買取に必要なもの</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {REQUIREMENTS.map((req) => (
              <div key={req.text} className="flex items-start gap-3 bg-[#F8F9F7] rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-[#B2AC88]/15 rounded-lg flex-shrink-0 mt-0.5">
                  <i className={`${req.icon} text-[#B2AC88] text-sm`} />
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{req.text}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-xs mt-4">
            ※ 古物営業法に基づき、買取の際は本人確認書類のご提示をお願いしております。
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowToSection;
